/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Grid from "@mui/material/Grid";
import { formValue } from "../../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import useForm from "../../../hooks/useForm";
import { matchRegexp, requiredFieldDefault, textFieldDefault } from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import ValidatorForm from "../../Validator/ValidatorForm";
import SubmitButton from "../../Button/SubmitButton";
import _ from "lodash";
import { Box } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";

const LegalRepresentative = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const initialValues = {
    hasLegalRepresentative: pcs.hasLegalRepresentative,
    "legalRepresentative.gender": pcs.legalRepresentative.gender,
    "legalRepresentative.givenName": pcs.legalRepresentative.givenName,
    "legalRepresentative.familyName": pcs.legalRepresentative.familyName,
    "legalRepresentative.birthName": pcs.legalRepresentative.birthName,
    "legalRepresentative.residenceAddress.streetAddress": pcs.legalRepresentative.residenceAddress.streetAddress,
    "legalRepresentative.residenceAddress.postalCode": pcs.legalRepresentative.residenceAddress.postalCode,
    "legalRepresentative.residenceAddress.addressLocality": pcs.legalRepresentative.residenceAddress.addressLocality,
    telephoneRepresentative: pcs.telephoneRepresentative,
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Gesetzlicher Vertreter</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Haben Sie einen gesetzlichen Vertreter?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Gesetzlicher Vertreter"}
              name={"hasLegalRepresentative"}
              value={formValue(values, "hasLegalRepresentative")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              dependentValidationFields={[
                "legalRepresentative.gender",
                "legalRepresentative.givenName",
                "legalRepresentative.familyName",
                "legalRepresentative.residenceAddress.streetAddress",
                "legalRepresentative.residenceAddress.postalCode",
                "legalRepresentative.residenceAddress.addressLocality",
                "telephoneRepresentative",
              ]}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Wie heißt Ihr gesetzlicher Vertreter?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Anrede"}
              fullWidth
              value={formValue(values, "legalRepresentative.gender")}
              onChange={handleChange}
              name={`legalRepresentative.gender`}
              inputProps={{
                name: `legalRepresentative.gender`,
                id: "gender",
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
              onBlur={handleBlur}
              validators={formValue(values, "hasLegalRepresentative") === "yes" ? requiredFieldDefault : []}
              registerValidators={registerValidators}
              error={!!errors["legalRepresentative.gender"]}
              helperText={errors["legalRepresentative.gender"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            >
              <MenuItem value={"female"}>Frau</MenuItem>
              <MenuItem value={"male"}>Herr</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="legalRepresentative.givenName"
              label="Vorname"
              autoComplete="given-name"
              margin="normal"
              value={formValue(values, "legalRepresentative.givenName")}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={
                formValue(values, "hasLegalRepresentative") === "yes"
                  ? [...requiredFieldDefault, ...textFieldDefault]
                  : []
              }
              registerValidators={registerValidators}
              error={!!errors["legalRepresentative.givenName"]}
              helperText={errors["legalRepresentative.givenName"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="legalRepresentative.familyName"
              label="Nachname"
              autoComplete="family-name"
              margin="normal"
              value={formValue(values, "legalRepresentative.familyName")}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={
                formValue(values, "hasLegalRepresentative") === "yes"
                  ? [...requiredFieldDefault, ...textFieldDefault]
                  : []
              }
              registerValidators={registerValidators}
              error={!!errors["legalRepresentative.familyName"]}
              helperText={errors["legalRepresentative.familyName"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            />
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Wo wohnt Ihr gesetzlicher Vertreter?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="legalRepresentative.residenceAddress.streetAddress"
              label="Straße und Hausnummer"
              autoComplete="address-line1"
              margin="normal"
              value={formValue(values, "legalRepresentative.residenceAddress.streetAddress")}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={
                formValue(values, "hasLegalRepresentative") === "yes"
                  ? [
                      ...requiredFieldDefault,
                      {
                        validator: matchRegexp,
                        params: "^[a-zA-ZäÄöÖüÜß]{2}.*[0-9]+.*$",
                        message: "Straße und Hausnummer im richtigen Format angeben",
                      },
                    ]
                  : []
              }
              registerValidators={registerValidators}
              error={!!errors["legalRepresentative.residenceAddress.streetAddress"]}
              helperText={errors["legalRepresentative.residenceAddress.streetAddress"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="legalRepresentative.residenceAddress.postalCode"
              label="Postleitzahl"
              autoComplete="postal-code"
              margin="normal"
              value={formValue(values, "legalRepresentative.residenceAddress.postalCode")}
              onBlur={handleBlur}
              validators={
                formValue(values, "hasLegalRepresentative") === "yes"
                  ? [
                      ...requiredFieldDefault,
                      {
                        validator: matchRegexp,
                        params: "^[0-9]{3,6}$",
                        message: "Die Postleitzahl besteht aus 3-6 Zahlen",
                      },
                    ]
                  : []
              }
              registerValidators={registerValidators}
              onChange={handleChange}
              error={!!errors["legalRepresentative.residenceAddress.postalCode"]}
              helperText={errors["legalRepresentative.residenceAddress.postalCode"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="legalRepresentative.residenceAddress.addressLocality"
              label="Ort"
              autoComplete="address-level2"
              margin="normal"
              value={formValue(values, "legalRepresentative.residenceAddress.addressLocality")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              validators={
                formValue(values, "hasLegalRepresentative") === "yes"
                  ? [...requiredFieldDefault, ...textFieldDefault]
                  : []
              }
              error={!!errors["legalRepresentative.residenceAddress.addressLocality"]}
              helperText={errors["legalRepresentative.residenceAddress.addressLocality"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            />
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Wie kann man Ihren gesetzlichen Vertreter telefonisch erreichen?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="telephoneRepresentative"
              label="Telefonnummer"
              autoComplete="tel"
              margin="normal"
              value={formValue(values, "telephoneRepresentative")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              validators={
                formValue(values, "hasLegalRepresentative") === "yes"
                  ? [
                      ...requiredFieldDefault,
                      {
                        validator: matchRegexp,
                        params: "^[0-9 -/+]*$",
                        message: "Das Format der Telefonnummer ist ungültig!",
                      },
                    ]
                  : []
              }
              error={!!errors["telephoneRepresentative"]}
              helperText={errors["telephoneRepresentative"]}
              disabled={formValue(values, "hasLegalRepresentative") !== "yes"}
            />
          </Grid>
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default LegalRepresentative;
